/**
 * The rename notice on a document (plan step 2,
 * docs/plans/2026-09-24-dictionary-rename-downstream.md).
 *
 * No document is changed automatically after a rename. This notice names each
 * renamed field and stays until the user chooses:
 *
 *   Regenerate               the card's own generator; any regeneration clears
 *                            the notice (the server watches the document)
 *   Update field names only  shows what will change, then applies it; hand
 *                            edits are kept. A backstop line stays.
 *   Dismiss                  the backstop only, with a reason. A rename the
 *                            ledger found stays on the notice.
 *
 * Every choice is recorded in the document's audit trail by the server
 * (resolveRenameNoticeV2). The notice is read live from
 * projects/{p}/renameNotices/{documentKey}; the browser can never clear it.
 */
import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { db, functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { usePermission } from '../../components/permissions/PermissionTooltip';
import { renameNoticeText, documentKeyOf, REGENERATE_ONLY_REASONS, type RenameNoticeLike } from './renameNoticeText';

interface Preview {
  supported: boolean;
  unsupportedReason: string | null;
  counts: Record<string, number>;
  total: number;
  collisions: string[];
}

/** A refusal the server wrote for a person to read; anything else gets a plain sentence. */
function messageOf(err: unknown, fallback: string): string {
  const e = err as { code?: string; message?: string };
  return e?.code === 'functions/failed-precondition' && e.message ? e.message : fallback;
}

export default function RenameNotice({
  projectId,
  documentPath,
  onRegenerate,
  regenerating = false,
  compact = false,
  documentLabel,
}: {
  projectId: string;
  /** The document's stored path, under the study ("dataManagement/sdtmMapping") or full ("projects/{id}/..."). */
  documentPath: string;
  /** The card's own Regenerate. Absent: the notice says where to regenerate. */
  onRegenerate?: () => void;
  regenerating?: boolean;
  compact?: boolean;
  /** Names the document when the notice does not sit on its own card. */
  documentLabel?: string;
}) {
  const documentKey = documentKeyOf(documentPath);
  // The completion guide and the annotated CRF can only be regenerated.
  const regenerateOnlyReason = REGENERATE_ONLY_REASONS[documentKey];
  const canEdit = usePermission('edit', projectId);
  const canGenerate = usePermission('generate', projectId);
  const [notice, setNotice] = useState<RenameNoticeLike | null>(null);
  const [mode, setMode] = useState<'idle' | 'previewing' | 'confirmUpdate' | 'applying' | 'dismissing' | 'savingDismiss'>('idle');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    const unsub = onSnapshot(
      doc(db, 'projects', projectId, 'renameNotices', documentKey),
      (snap) => setNotice(snap.exists() ? (snap.data() as RenameNoticeLike) : null),
      () => setNotice(null),
    );
    return () => unsub();
  }, [projectId, documentKey]);

  const text = useMemo(() => renameNoticeText(notice), [notice]);
  if (!text.headline) {
    return done ? <p className="text-xs text-green-700 mb-3">{done}</p> : null;
  }

  const call = httpsCallable(functions, CLOUD_FUNCTIONS.RESOLVE_RENAME_NOTICE, { timeout: CALLABLE_TIMEOUTS.LONG });

  const startUpdate = async () => {
    setError(null); setDone(null); setMode('previewing');
    try {
      const res = await call({ projectId, documentKey, action: 'preview' });
      setPreview(res.data as Preview);
      setMode('confirmUpdate');
    } catch (err) {
      setError(messageOf(err, 'Could not work out what would change. Please try again.'));
      setMode('idle');
    }
  };

  const applyUpdate = async () => {
    setError(null); setMode('applying');
    try {
      const res = await call({ projectId, documentKey, action: 'updateNames' });
      const out = res.data as { applied: boolean; collisions: string[]; unsupportedReason?: string };
      if (!out.applied) {
        setError(out.unsupportedReason || `Nothing was changed: ${out.collisions.join('; ')}.`);
      } else {
        setDone('Field names updated. The change is recorded in this document\'s history.');
      }
    } catch (err) {
      setError(messageOf(err, 'The field names could not be updated. Nothing was changed.'));
    }
    setPreview(null);
    setMode('idle');
  };

  const saveDismiss = async () => {
    setError(null); setMode('savingDismiss');
    try {
      await call({ projectId, documentKey, action: 'dismissBackstop', reason });
      setDone('Notice dismissed. Your reason is recorded in this document\'s history.');
      setReason('');
    } catch (err) {
      setError(messageOf(err, 'The notice could not be dismissed. Please try again.'));
    }
    setMode('idle');
  };

  const busy = mode === 'previewing' || mode === 'applying' || mode === 'savingDismiss';
  const btn = 'text-xs font-medium px-2.5 py-1 rounded-md border disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className={`rounded-lg border border-amber-200 bg-amber-50 ${compact ? 'p-2 mt-2' : 'p-3 mb-4'} text-xs text-amber-900`} data-testid="rename-notice" data-document-key={documentKey}>
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600 mt-0.5" />
        <div className="flex-1 space-y-1.5">
          <p className="font-medium">{documentLabel ? `${documentLabel}: ` : ''}{text.headline}</p>
          {text.renameLines.length > 0 && (
            <ul className="list-disc pl-4 space-y-0.5">
              {text.renameLines.map((l) => <li key={l.ledgerId}>{l.text}</li>)}
            </ul>
          )}
          {text.backstopLines.map((l) => <p key={l.backstopId}>{l.text}</p>)}
          {regenerateOnlyReason && text.hasRenames && <p className="italic">{regenerateOnlyReason}</p>}

          {mode === 'confirmUpdate' && preview && (
            <div className="rounded-md bg-white border border-amber-200 p-2 space-y-1.5">
              {!preview.supported ? (
                <p>{preview.unsupportedReason}</p>
              ) : preview.collisions.length > 0 ? (
                <p className="text-red-700">Nothing can be changed: {preview.collisions.join('; ')}. Resolve it, or regenerate the document.</p>
              ) : (
                <p>
                  {preview.total === 0
                    ? 'None of the old names appear in this document any more, so nothing in it changes; applying clears the renames from this notice.'
                    : `This changes the old name${text.renameLines.length === 1 ? '' : 's'} in ${preview.total} place${preview.total === 1 ? '' : 's'} (fields, rows or paragraphs) and nothing else; hand edits are kept.`}
                  {text.hasBackstops && ' The backstop line stays until the document is regenerated or the notice is dismissed.'}
                </p>
              )}
              <div className="flex gap-2">
                {preview.supported && preview.collisions.length === 0 && (
                  <button type="button" className={`${btn} bg-amber-600 text-white border-amber-600`} onClick={applyUpdate}>
                    {preview.total === 0 ? 'Clear these renames' : `Update ${preview.total} place${preview.total === 1 ? '' : 's'}`}
                  </button>
                )}
                <button type="button" className={`${btn} bg-white border-gray-300 text-gray-700`} onClick={() => { setMode('idle'); setPreview(null); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {(mode === 'dismissing' || mode === 'savingDismiss') && (
            <div className="rounded-md bg-white border border-amber-200 p-2 space-y-1.5">
              <label className="block font-medium" htmlFor={`dismiss-${documentKey}`}>
                Why is this document still right? (recorded in its history)
              </label>
              <textarea
                id={`dismiss-${documentKey}`}
                className="w-full rounded border border-gray-300 p-1.5 text-xs text-gray-900"
                rows={2}
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Checked form RTW: only the renamed field changed."
              />
              <div className="flex gap-2">
                <button type="button" className={`${btn} bg-amber-600 text-white border-amber-600`} disabled={reason.trim().length < 3 || busy} onClick={saveDismiss}>
                  {mode === 'savingDismiss' ? 'Dismissing…' : 'Dismiss notice'}
                </button>
                <button type="button" className={`${btn} bg-white border-gray-300 text-gray-700`} disabled={busy} onClick={() => { setMode('idle'); setReason(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {error && <p className="text-red-700">{error}</p>}

          {mode === 'idle' && (
            <div className="flex flex-wrap gap-2 pt-0.5">
              {onRegenerate ? (
                <button type="button" className={`${btn} bg-white border-amber-300 text-amber-900`} disabled={!canGenerate || regenerating} onClick={onRegenerate}
                  title={canGenerate ? 'Rebuild the document from the current forms. Hand edits are lost.' : "Your role doesn't allow regenerating documents."}>
                  {regenerating ? 'Regenerating…' : 'Regenerate'}
                </button>
              ) : null}
              {text.hasRenames && !regenerateOnlyReason && (
                <button type="button" className={`${btn} bg-white border-amber-300 text-amber-900`} disabled={!canEdit} onClick={startUpdate}
                  title={canEdit ? 'Replace the old names with the new ones and change nothing else.' : "Your role doesn't allow editing documents."}>
                  Update field names only
                </button>
              )}
              {text.hasBackstops && (
                <button type="button" className={`${btn} bg-white border-amber-300 text-amber-900`} disabled={!canEdit} onClick={() => { setError(null); setDone(null); setMode('dismissing'); }}
                  title={canEdit ? 'You have reviewed the form and this document is still right.' : "Your role doesn't allow editing documents."}>
                  Dismiss
                </button>
              )}
            </div>
          )}
          {mode === 'previewing' && <p className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Working out what would change…</p>}
          {mode === 'applying' && <p className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Updating field names…</p>}
          {done && <p className="text-green-700">{done}</p>}
        </div>
      </div>
    </div>
  );
}
